import {
  formatMilestoneDate,
  formatMilestoneTimestamp,
  getTransactionKind,
  InvestmentTransaction,
} from "./InvestmentTracker";
import { ProgressStep } from "./TransactionProgressTracker";
import {
  DetailSummaryRow,
  TransactionDetailLayout,
  TransactionDetailModel,
} from "./TransactionDetailLayout";

type RedemptionStage =
  | "requested"
  | "confirmed"
  | "waitingForProceeds"
  | "credited"
  | "declined";

type RedemptionProgress = {
  version: 1;
  stage: RedemptionStage;
  requestedAt?: unknown;
  confirmedAt?: unknown;
  waitingForProceedsAt?: unknown;
  creditedAt?: unknown;
  declinedAt?: unknown;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function getTrackedProgress(value: unknown): RedemptionProgress | null {
  const progress = asRecord(value);
  const validStages: RedemptionStage[] = [
    "requested",
    "confirmed",
    "waitingForProceeds",
    "credited",
    "declined",
  ];

  if (
    progress.version !== 1 ||
    typeof progress.stage !== "string" ||
    !validStages.includes(progress.stage as RedemptionStage)
  ) {
    return null;
  }

  return progress as RedemptionProgress;
}

function toFiniteNumber(value: unknown): number | null {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatCurrency(value: unknown): string {
  const amount = toFiniteNumber(value);
  if (amount === null) return "Pending confirmation";

  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatNumber(value: unknown): string {
  const number = toFiniteNumber(value);
  if (number === null) return "Pending confirmation";

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 8,
  }).format(number);
}

function formatNavps(value: unknown): string {
  const number = toFiniteNumber(value);
  if (number === null) return "Pending confirmation";

  return `₱${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(number)}`;
}

function timestamp(value: unknown) {
  return value ? formatMilestoneTimestamp(value) : undefined;
}

export function deriveRedemptionSteps(
  transaction: InvestmentTransaction,
  progress: RedemptionProgress,
  redemptionSettlementTimeline?: string
): ProgressStep[] {
  const statesByStage: Record<RedemptionStage, ProgressStep["state"][]> = {
    requested: ["complete", "current", "upcoming"],
    confirmed: ["complete", "complete", "current"],
    waitingForProceeds: ["complete", "complete", "current"],
    credited: ["complete", "complete", "complete"],
    declined: ["complete", "failed", "upcoming"],
  };
  const chipFor = (state: ProgressStep["state"]): ProgressStep["chip"] => {
    if (state === "complete") return "Completed";
    if (state === "current") return "In progress";
    if (state === "failed") return "Declined";
    return "Pending";
  };

  const definitions = [
    {
      id: "requested",
      title: "Redemption Requested",
      description: "We received your redemption request.",
      timestamp: transaction.datePlaced ?? progress.requestedAt,
      icon: "request" as const,
    },
    {
      id: "confirmed",
      title:
        progress.stage === "declined"
          ? "Redemption Declined"
          : progress.stage === "requested"
          ? "Processing Redemption"
          : "Redemption Confirmed",
      description:
        progress.stage === "declined"
          ? "We could not process this redemption. Contact Support if you need assistance."
          : progress.stage === "requested"
          ? "We are reviewing your redemption request."
          : "Your redemption details have been confirmed for processing.",
      timestamp:
        progress.stage === "declined"
          ? progress.declinedAt
          : progress.stage === "requested"
          ? undefined
          : transaction.dateProcessed,
      icon: "processing" as const,
    },
    {
      id: "settled",
      title:
        progress.stage === "credited"
          ? "Redemption Settled"
          : "Processing Settlement",
      description:
        progress.stage === "credited"
          ? "Your redemption proceeds have been credited to your bank account."
          : redemptionSettlementTimeline?.trim()
          ? `Your redemption has been booked and will be reflected within ${redemptionSettlementTimeline.trim()} days.`
          : "Your redemption has been booked and is being prepared for settlement.",
      timestamp:
        progress.stage === "credited" ? progress.creditedAt : undefined,
      icon: "processing" as const,
    },
  ];

  return definitions.map((definition, index) => {
    const state = statesByStage[progress.stage][index];
    return {
      ...definition,
      number: index + 1,
      state,
      chip: chipFor(state),
      timestamp: timestamp(definition.timestamp),
    };
  });
}

function chipForState(
  state: ProgressStep["state"]
): ProgressStep["chip"] {
  if (state === "complete") return "Completed";
  if (state === "current") return "In progress";
  if (state === "failed") return "Declined";
  return "Pending";
}

function legacyStep(
  number: number,
  value: Omit<ProgressStep, "number" | "chip" | "timestamp"> & {
    timestamp?: unknown;
  }
): ProgressStep {
  return {
    ...value,
    number,
    chip: chipForState(value.state),
    timestamp: timestamp(value.timestamp),
  };
}

export function deriveLegacyRedemptionSteps(
  transaction: InvestmentTransaction
): ProgressStep[] {
  const status = transaction.status;
  const isDeclined = status === "declined" || status === "declinedredemption";
  const isRedeemed = status === "redeemed";
  const isPending =
    status === "pendingwithdrawal" ||
    status === "pendingRedemption" ||
    status === "processingRedemption";
  const secondState: ProgressStep["state"] = isDeclined
    ? "failed"
    : isRedeemed
    ? "complete"
    : "current";

  return [
    legacyStep(1, {
      id: "requested",
      title: "Redemption Requested",
      description: "We received your redemption request.",
      state: "complete",
      timestamp: transaction.datePlaced,
      icon: "request",
    }),
    legacyStep(2, {
      id: "confirmed",
      title: isDeclined
        ? "Redemption Declined"
        : isRedeemed
        ? "Redemption Confirmed"
        : "Processing Redemption",
      description: isDeclined
        ? "We could not process this redemption. Contact Support if you need assistance."
        : isRedeemed
        ? "Your redemption was processed before bank-credit tracking was available."
        : "We are reviewing your redemption request.",
      state: secondState,
      timestamp: isPending ? undefined : transaction.dateProcessed,
      icon: "processing",
    }),
    legacyStep(3, {
      id: "settled",
      title: "Processing Settlement",
      description: "Settlement progress is unavailable for this historical redemption.",
      state: "upcoming",
      icon: "processing",
    }),
  ];
}

function buildSummaryRows(
  transaction: InvestmentTransaction,
  progress: RedemptionProgress | null
): DetailSummaryRow[] {
  const fundDetails = asRecord(transaction.fundDetails);
  const fundName =
    typeof fundDetails.fundName === "string" ? fundDetails.fundName : "-";
  const processed =
    progress === null ||
    ["confirmed", "waitingForProceeds", "credited"].includes(
      progress.stage
    );
  const finalValue = (value: unknown, formatter = formatCurrency) =>
    processed
      ? formatter(value)
      : progress?.stage === "declined"
      ? "Not available"
      : "Pending confirmation";

  return [
    { label: "Fund Name", value: fundName },
    { label: "Redemption Amount", value: formatCurrency(transaction.amount) },
    { label: "Units Redeemed", value: formatNumber(transaction.shares) },
    {
      label: "NAVPU / Rate",
      value: finalValue(transaction.rateAtBooking, formatNavps),
    },
    { label: "Market Value", value: finalValue(transaction.marketValue) },
    { label: "Flat Service Fee", value: finalValue(transaction.serviceFee) },
    { label: "Exit Fee", value: finalValue(transaction.exitFee) },
    {
      label: "Net Redemption Proceeds",
      value: finalValue(transaction.netPay),
    },
    {
      label: "Date Requested",
      value: formatMilestoneDate(transaction.datePlaced),
    },
    {
      label: "Date Processed",
      value: transaction.dateProcessed
        ? formatMilestoneDate(transaction.dateProcessed)
        : "In progress",
      tone: transaction.dateProcessed ? "normal" : "muted",
    },
  ];
}

export function deriveRedemptionDetailModel(
  transaction: InvestmentTransaction,
  redemptionSettlementTimeline?: string
): TransactionDetailModel | null {
  if (getTransactionKind(transaction) !== "redemption") {
    return null;
  }

  const progress = getTrackedProgress(transaction.redemptionProgress);

  const fundDetails = asRecord(transaction.fundDetails);
  const fundCompanyDetails = asRecord(transaction.fundCompanyDetails);
  const fundName =
    typeof fundDetails.fundName === "string"
      ? fundDetails.fundName
      : "Redemption";
  const companyName =
    typeof fundCompanyDetails.fundCompanyName === "string"
      ? fundCompanyDetails.fundCompanyName
      : "Rampver Investment";
  const eventId =
    typeof transaction.eventId === "string" ? transaction.eventId : "-";
  const processed =
    progress === null ||
    ["confirmed", "waitingForProceeds", "credited"].includes(
      progress.stage
    );

  return {
    idPrefix: "redemption-detail",
    header: {
      title: fundName,
      metadata: [companyName, `Transaction ID ${eventId}`],
      metricLabel: processed
        ? "Net Redemption Proceeds"
        : "Estimated Redemption Value",
      metricValue: formatCurrency(
        processed ? transaction.netPay : transaction.amount
      ),
    },
    tracker: {
      title: "Redemption Tracker",
      ariaLabel: "Redemption progress steps",
      description: "Follow your redemption request through settlement.",
      showChips: false,
      steps:
        progress
          ? deriveRedemptionSteps(
              transaction,
              progress,
              redemptionSettlementTimeline
            )
          : deriveLegacyRedemptionSteps(transaction),
    },
    summary: {
      title: "Transaction Summary",
      rows: buildSummaryRows(transaction, progress),
      note:
        !progress
          ? "Bank-credit tracking was unavailable for this historical transaction."
          : progress.stage === "credited"
          ? undefined
          : "Bank posting times may vary after Rampver marks the proceeds as credited.",
    },
  };
}

export function RedemptionDetailView({
  transaction,
  redemptionSettlementTimeline,
}: {
  transaction: InvestmentTransaction;
  redemptionSettlementTimeline?: string;
}): JSX.Element | null {
  const model = deriveRedemptionDetailModel(
    transaction,
    redemptionSettlementTimeline
  );
  return model ? <TransactionDetailLayout model={model} /> : null;
}
